/**
 * Analítica pura do LST 2.0 — tendência mensal, Pareto de desvios e
 * performance comparativa por camada (liderados). Sem React/Supabase.
 */

import type { OrgMember } from '../../../hooks/useOrgMembers';
import type { LstCargo, LstExecucao, LstFrequency, LstTemplate } from '../types';
import { isTemplateDueOnDate } from './lstSchedule';
import { isLstSlotStillOpen } from '../utils/lstEffectiveStatus';
import { templateAppliesToUser } from '../utils/lstTemplateScope';

export interface LstSubject {
  userId: string;
  cargo: LstCargo;
  name: string;
  member?: OrgMember;
}

export interface TrendPoint {
  key: string; // YYYY-MM
  label: string;
  ok: number;
  desvio: number;
  perdido: number;
  base: number;
  pct: number; // aderência = (ok+desvio)/base
  conformidade: number; // ok/base
}

export interface ParetoItem {
  templateId: string;
  title: string;
  isCritical: boolean;
  ocorrencias: number; // desvios + perdas
  desvios: number;
  perdas: number;
  pct: number;
  pctAccum: number;
}

export interface MemberPerformance {
  userId: string;
  name: string;
  cargo: LstCargo;
  ok: number;
  desvio: number;
  perdido: number;
  base: number;
  pct: number;
  conformidade: number;
  trend: TrendPoint[];
}

type CellState = 'ok' | 'desvio' | 'perdido' | 'futuro' | 'na';

function blockIsDaily(freq: LstFrequency): boolean {
  return freq === 'horaria' || freq === 'diaria';
}

function classify(freq: LstFrequency, stillOpen: boolean, exec: LstExecucao | null): CellState {
  if (exec) {
    if (exec.status === 'realizado') return 'ok';
    if (exec.status === 'realizado_com_desvio') return 'desvio';
    if (exec.status === 'nao_realizado') return 'perdido';
  }
  if (stillOpen) return 'futuro';
  return blockIsDaily(freq) ? 'perdido' : 'na';
}

/** Todas as datas (YYYY-MM-DD) entre start e end (inclusive). */
export function datesBetween(start: string, end: string): string[] {
  const out: string[] = [];
  const [ys, ms, ds] = start.split('-').map(Number);
  const [ye, me, de] = end.split('-').map(Number);
  const cur = new Date(ys, ms - 1, ds);
  const last = new Date(ye, me - 1, de);
  while (cur <= last) {
    out.push(
      `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`,
    );
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

interface Counter {
  ok: number;
  desvio: number;
  perdido: number;
}

function emptyCounter(): Counter {
  return { ok: 0, desvio: 0, perdido: 0 };
}

function applyState(c: Counter, state: CellState): void {
  if (state === 'ok') c.ok += 1;
  else if (state === 'desvio') c.desvio += 1;
  else if (state === 'perdido') c.perdido += 1;
}

function pctOf(c: Counter): { base: number; pct: number; conformidade: number } {
  const base = c.ok + c.desvio + c.perdido;
  const pct = base === 0 ? 0 : Math.round(((c.ok + c.desvio) / base) * 100);
  const conformidade = base === 0 ? 0 : Math.round((c.ok / base) * 100);
  return { base, pct, conformidade };
}

export interface LstAnalyticsParams {
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  subjects: LstSubject[];
  dates: string[]; // intervalo já filtrado (asc)
  monthKeys: Array<{ key: string; label: string }>;
  today: string;
}

export interface LstAnalyticsResult {
  trend: TrendPoint[];
  pareto: ParetoItem[];
  perMember: MemberPerformance[];
  totals: Counter & { base: number; pct: number; conformidade: number; desviosAbertos: number };
}

/**
 * Núcleo analítico: cruza templates × execuções × intervalo de datas para
 * todos os sujeitos (usuário logado + liderados), gerando tendência, Pareto
 * e performance por camada num único passe.
 */
export function computeLstAnalytics(params: LstAnalyticsParams): LstAnalyticsResult {
  const { templates, execucoes, subjects, dates, monthKeys, today } = params;

  // Index execução por user__template__data.
  const execIndex = new Map<string, LstExecucao>();
  for (const e of execucoes) execIndex.set(`${e.user_id}__${e.template_id}__${e.data}`, e);

  const monthOrder = monthKeys.map((m) => m.key);
  const monthLabel = new Map(monthKeys.map((m) => [m.key, m.label]));

  const globalMonth = new Map<string, Counter>();
  const paretoMap = new Map<string, { title: string; isCritical: boolean; desvios: number; perdas: number }>();
  const perMember: MemberPerformance[] = [];
  const globalTotals = emptyCounter();
  let desviosAbertos = 0;

  for (const subj of subjects) {
    const tpls = templates.filter((t) => templateAppliesToUser(t, subj.cargo, subj.userId));
    const memberMonth = new Map<string, Counter>();
    const memberTotal = emptyCounter();

    for (const tpl of tpls) {
      for (const date of dates) {
        if (!isTemplateDueOnDate(tpl, date, dates)) continue;

        const exec = execIndex.get(`${subj.userId}__${tpl.id}__${date}`) ?? null;
        const state = classify(
          tpl.frequency,
          !exec && isLstSlotStillOpen(tpl.scheduled_time, date, today),
          exec,
        );
        if (state === 'futuro' || state === 'na') continue;

        const mk = date.slice(0, 7);
        // Mês (global + membro)
        const gm = globalMonth.get(mk) ?? emptyCounter();
        applyState(gm, state);
        globalMonth.set(mk, gm);
        const mm = memberMonth.get(mk) ?? emptyCounter();
        applyState(mm, state);
        memberMonth.set(mk, mm);

        applyState(memberTotal, state);
        applyState(globalTotals, state);

        // Pareto (apenas anormalidades)
        if (state === 'desvio' || state === 'perdido') {
          const p = paretoMap.get(tpl.id) ?? {
            title: tpl.title,
            isCritical: tpl.is_critical,
            desvios: 0,
            perdas: 0,
          };
          if (state === 'desvio') p.desvios += 1;
          else p.perdas += 1;
          paretoMap.set(tpl.id, p);
        }
        // Desvios "abertos" — desvio com ação vinculada ainda em aberto não é
        // calculável aqui; usamos a contagem de desvios do período como proxy.
        if (state === 'desvio') desviosAbertos += 1;
      }
    }

    const memberTrend: TrendPoint[] = monthOrder.map((key) => {
      const c = memberMonth.get(key) ?? emptyCounter();
      const { base, pct, conformidade } = pctOf(c);
      return { key, label: monthLabel.get(key) ?? key, ...c, base, pct, conformidade };
    });
    const mt = pctOf(memberTotal);
    perMember.push({
      userId: subj.userId,
      name: subj.name,
      cargo: subj.cargo,
      ...memberTotal,
      base: mt.base,
      pct: mt.pct,
      conformidade: mt.conformidade,
      trend: memberTrend,
    });
  }

  const trend: TrendPoint[] = monthOrder.map((key) => {
    const c = globalMonth.get(key) ?? emptyCounter();
    const { base, pct, conformidade } = pctOf(c);
    return { key, label: monthLabel.get(key) ?? key, ...c, base, pct, conformidade };
  });

  // Pareto ordenado por ocorrências + percentual acumulado.
  const paretoRaw = Array.from(paretoMap.entries()).map(([templateId, v]) => ({
    templateId,
    title: v.title,
    isCritical: v.isCritical,
    desvios: v.desvios,
    perdas: v.perdas,
    ocorrencias: v.desvios + v.perdas,
  }));
  paretoRaw.sort((a, b) => b.ocorrencias - a.ocorrencias);
  const totalOcorr = paretoRaw.reduce((s, p) => s + p.ocorrencias, 0);
  let accum = 0;
  const pareto: ParetoItem[] = paretoRaw.map((p) => {
    const pct = totalOcorr === 0 ? 0 : Math.round((p.ocorrencias / totalOcorr) * 100);
    accum += p.ocorrencias;
    const pctAccum = totalOcorr === 0 ? 0 : Math.round((accum / totalOcorr) * 100);
    return { ...p, pct, pctAccum };
  });

  const gt = pctOf(globalTotals);
  perMember.sort((a, b) => b.pct - a.pct);

  return {
    trend,
    pareto,
    perMember,
    totals: { ...globalTotals, base: gt.base, pct: gt.pct, conformidade: gt.conformidade, desviosAbertos },
  };
}
